/**
 * The Star adapter: the existing `starPrintService` behind the shared
 * `PrinterAdapter` contract, unchanged underneath.
 *
 * Nothing about Star's lifecycle moves here. `starPrintService` still owns its
 * one connection, its lazy reconnect from the saved default, its stale-
 * connection retry and the `withLock` mutex; this adapter only translates the
 * shared vocabulary (job outcomes, unified status) to and from it.
 */
import { starPrintService } from '../star-print.service';
import type {
  StarPrintResult,
  StarPrinterStatus,
  StarPrintReceiptItem,
  StarPrintOperationResult,
} from '../star-print.types';
import type {
  SavedPrinter,
  PrinterStatus,
  PrinterAdapter,
  PrintJobResult,
  PrinterCapabilities,
  PrinterStatusListener,
} from '../printer-types';

/** Star's own vocabulary for "the write was interrupted; part may be on paper". */
function outcomeOf(result: StarPrintResult): PrintJobResult['outcome'] {
  if (result.success) return 'submitted';
  const code = String(result.error?.code ?? '').toUpperCase();
  if (code === 'PRINT_FAILED' && result.error?.recoverable === false) return 'delivery-unknown';
  return 'not-sent';
}

function toJobResult(
  printer: SavedPrinter,
  jobId: string,
  result: StarPrintOperationResult
): PrintJobResult {
  return {
    success: result.success,
    outcome: outcomeOf(result),
    ...(result.cancelled ? { cancelled: true } : {}),
    jobId,
    printerId: printer.identifier,
    status: result.status,
    message: result.message,
    ...(result.error
      ? {
          error: {
            code: String(result.error.code),
            message: result.error.message,
            recoverable: result.error.recoverable,
          },
        }
      : {}),
  };
}

function toBatchItems(images: readonly string[]): StarPrintReceiptItem[] {
  return images.flatMap((imageBase64, index) => {
    const image: StarPrintReceiptItem = { type: 'image', imageBase64 };
    if (index === images.length - 1) return [image];
    // StarPrint 1.4.4 adds an in-stream partial cut receipt item. Keeping it
    // between images produces independently cut documents in one native job.
    const cut: StarPrintReceiptItem = { type: 'cut' };
    return [image, cut];
  });
}

export const starPrinterAdapter: PrinterAdapter = {
  driver: 'star',

  capabilities(printer: SavedPrinter): PrinterCapabilities {
    return {
      driver: 'star',
      transports: [printer.interfaceType],
      statusReporting: 'realtime',
      rasterImages: true,
      cutter: true,
      cashDrawer: true,
    };
  },

  async ensureConnected(printer: SavedPrinter): Promise<void> {
    if (printer.driver === 'escpos') throw new Error('Not a Star printer.');
    if (starPrintService.getActiveConnectionId() !== null) return;
    await starPrintService.connectPrinter(printer.connection);
  },

  async disconnect(): Promise<void> {
    await starPrintService.disconnectPrinter();
  },

  async getStatus(_printer: SavedPrinter, options): Promise<PrinterStatus> {
    return starPrintService.getPrinterStatus({ forceReconnect: options.forceReconnect });
  },

  async printImages(printer, images, options): Promise<PrintJobResult> {
    const result =
      images.length === 1
        ? await starPrintService.printImage(images[0] ?? '', {
            cutPaper: true,
            jobId: options.jobId,
            openCashDrawer: options.openCashDrawer,
            isCurrent: options.isCurrent,
          })
        : await starPrintService.printReceipt(toBatchItems(images), {
            cutPaper: true,
            jobId: options.jobId,
            openCashDrawer: options.openCashDrawer,
            isCurrent: options.isCurrent,
          });
    return toJobResult(printer, options.jobId, result);
  },

  async openCashDrawer(printer, options): Promise<PrintJobResult> {
    return toJobResult(printer, options.jobId, await starPrintService.openCashDrawer());
  },

  subscribeStatus(listener: PrinterStatusListener): () => void {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;
    // The Star service registers its plugin listeners lazily and asynchronously.
    starPrintService
      .addStatusListener((status: StarPrinterStatus, connectionId?: string) => {
        if (cancelled) return;
        // The desktop LAN backend's connection id IS the saved identifier
        // (`lan:<ip>:<port>`); the mobile SDK's is an opaque handle, which the
        // store resolves to "the active Star printer".
        listener(connectionId ?? null, status);
      })
      .then((remove) => {
        if (cancelled) remove();
        else unsubscribe = remove;
      })
      .catch((error: unknown) => {
        console.warn('[StarPrinterAdapter] status subscription failed:', error);
      });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  },
};
